/**
 * Fly-to-cart — Horizon-style path from Add-to-cart button → header cart icon.
 * Curve comes from separate X/Y CSS easings (animation-composition: accumulate).
 */
class FlyToCart extends HTMLElement {
  /** @type {Element | null} */
  source = null;

  /** @type {Element | null} */
  destination = null;

  /** @type {boolean} */
  useSourceSize = false;

  connectedCallback() {
    if (!(this.source instanceof Element) || !(this.destination instanceof Element)) {
      this.remove();
      return;
    }

    requestAnimationFrame(() => {
      const sourceRect = this.source.getBoundingClientRect();
      const destinationRect = this.destination.getBoundingClientRect();
      this.#animate(sourceRect, destinationRect);
    });
  }

  /**
   * @param {DOMRectReadOnly} sourceRect
   * @param {DOMRectReadOnly} destinationRect
   */
  #animate = async (sourceRect, destinationRect) => {
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2,
      y: sourceRect.top + sourceRect.height / 2,
    };
    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2,
      y: destinationRect.top + destinationRect.height / 2,
    };

    if (this.useSourceSize) {
      this.style.setProperty('--width', `${Math.max(24, Math.round(sourceRect.width))}px`);
      this.style.setProperty('--height', `${Math.max(24, Math.round(sourceRect.height))}px`);
    }

    this.style.setProperty('--start-x', `${startPoint.x}px`);
    this.style.setProperty('--start-y', `${startPoint.y}px`);
    this.style.setProperty('--travel-x', `${endPoint.x - startPoint.x}px`);
    this.style.setProperty('--travel-y', `${endPoint.y - startPoint.y}px`);

    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    try {
      const animations = typeof this.getAnimations === 'function' ? this.getAnimations({ subtree: false }) : [];
      if (animations.length) {
        await Promise.allSettled(animations.map((a) => a.finished));
      } else {
        await new Promise((resolve) => window.setTimeout(resolve, 900));
      }
    } catch (_) {
      await new Promise((resolve) => window.setTimeout(resolve, 900));
    }

    const cartSlot = this.destination?.closest?.('.cartCounter') || this.destination;
    if (cartSlot instanceof Element) {
      cartSlot.classList.add('is-fly-to-cart-hit');
      window.setTimeout(() => cartSlot.classList.remove('is-fly-to-cart-hit'), 480);
    }

    this.remove();
  };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}

/**
 * @param {Element | null} context
 * @returns {string}
 */
function howssResolveFlyImage(context) {
  const scope =
    (context instanceof Element &&
      context.closest('product-form, .product-page, .quick-add-drawer, .card, product-card, .product-card')) ||
    document;

  const attrHost =
    (context instanceof Element &&
      (context.closest('[data-product-variant-media]') ||
        context.closest('product-form') ||
        context.closest('form[data-product-variant-media]'))) ||
    null;
  const fromAttr = attrHost?.getAttribute?.('data-product-variant-media');
  if (fromAttr) return fromAttr;

  const candidates = [
    scope.querySelector?.('media-gallery .slider__slide.is-active img'),
    scope.querySelector?.('.bullet-product-media .slider__slide.is-active img'),
    scope.querySelector?.('.bullet-product-media img'),
    scope.querySelector?.('.product-page [id^="Slider-Gallery"] .slider__slide.is-active img'),
    scope.querySelector?.('.product-page .product__media img, .product-page .media img'),
    scope.querySelector?.('.quick-add-drawer .drawer-body img'),
    scope.querySelector?.('.card-image img, .card__media img, .product-card img'),
    document.querySelector('.product-page media-gallery img, .product-page .bullet-product-media img'),
  ].filter(Boolean);

  for (const img of candidates) {
    if (!(img instanceof HTMLImageElement)) continue;
    const src = img.currentSrc || img.src || img.getAttribute('src');
    if (src && !src.startsWith('data:')) return src;
  }

  return '';
}

/**
 * @returns {Element | null}
 */
function howssGetCartIconTarget() {
  return (
    document.querySelector('.cartCounter button[data-open="#cartBox"]') ||
    document.querySelector('.cartCounter button, .cartCounter a') ||
    document.querySelector('.cartCounter #counter') ||
    document.querySelector('.cartCounter')
  );
}

/**
 * @param {{ source?: Element | null, form?: Element | null, imageUrl?: string, variant?: 'main' | 'quick' | 'sticky' }} [opts]
 */
window.__howssAnimateFlyToCart = function howssAnimateFlyToCart(opts = {}) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const now = Date.now();
  if (window.__howssFlyToCartLock && now - window.__howssFlyToCartLock < 450) return;
  window.__howssFlyToCartLock = now;

  const destination = howssGetCartIconTarget();
  if (!destination) return;

  const form =
    (opts.form instanceof HTMLFormElement && opts.form) ||
    (opts.source instanceof Element && opts.source.closest('form')) ||
    null;

  // Start from Add to cart button → header cart icon
  let source =
    (opts.source instanceof Element &&
      (opts.source.closest('button[type="submit"], button.cartButton, [name="add"]') ||
        (opts.source.matches?.('button, [type="submit"]') ? opts.source : null))) ||
    form?.querySelector(
      '[type="submit"][name="add"], button.cartButton[type="submit"], button.cartButton, button[type="submit"]'
    ) ||
    (opts.source instanceof Element ? opts.source : null) ||
    document.activeElement;

  if (!(source instanceof Element)) return;

  const imageUrl = opts.imageUrl || howssResolveFlyImage(form || source);
  if (!imageUrl) return;

  let variant = opts.variant;
  if (!variant) {
    if (source.closest('#add2cart-cta') || source.closest('.sticky-add-to-cart')) variant = 'sticky';
    else if (source.closest('.quick-add-drawer')) variant = 'quick';
    else variant = 'main';
  }

  document.querySelectorAll('fly-to-cart').forEach((node, index) => {
    if (index > 0) node.remove();
  });

  const flyer = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
  flyer.classList.add(`fly-to-cart--${variant}`);
  flyer.style.backgroundImage = `url(${JSON.stringify(imageUrl)})`;
  flyer.style.setProperty('--start-opacity', '0');
  flyer.source = source;
  flyer.destination = destination;
  flyer.useSourceSize = false;

  document.body.appendChild(flyer);
};
